import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WorkflowBuilder from '../components/WorkflowBuilder';
import { cloneCampaignWorkflow, countWorkflowSteps } from '../utils/campaignTemplate';
import { API_BASE_URL } from '../config/api';
import './CampaignPage.css';

function CampaignPage({ accountId: propAccountId, onCreationStateChange }) {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'stats'
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);

  // 优先使用 props 传入的 accountId（从后端获取），如果没有则使用 localStorage（向后兼容）
  const storedAccountId = localStorage.getItem('accountId');
  const storedLinkedinAccountId = localStorage.getItem('linkedin_account_id');
  const accountId = propAccountId || storedAccountId || storedLinkedinAccountId || null;

  // 调试日志：检查 accountId 的值和来源
  useEffect(() => {
    console.log('🔍 CampaignPage accountId 诊断:', {
      propAccountId,
      storedAccountId,
      storedLinkedinAccountId,
      finalAccountId: accountId,
      accountIdType: typeof accountId,
      accountIdLength: accountId?.length,
      isEmpty: !accountId || accountId === ''
    });

    if (!accountId || accountId === '') {
      console.warn('⚠️ CampaignPage: accountId 为空，无法加载数据');
      console.warn('   请检查：1) 是否已登录 2) localStorage 中是否有 accountId');
    }
  }, [propAccountId, storedAccountId, storedLinkedinAccountId, accountId]);

  // 如果从 props 获取到 accountId，同步到 localStorage
  useEffect(() => {
    if (propAccountId && propAccountId !== localStorage.getItem('accountId')) {
      console.log('✅ CampaignPage: 同步 accountId 到 localStorage:', propAccountId);
      localStorage.setItem('accountId', propAccountId);
      localStorage.setItem('linkedin_account_id', propAccountId);
    }
  }, [propAccountId]);

  // Campaign form state
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [workflow, setWorkflow] = useState({
    nodes: [{ id: 'start', type: 'start', label: 'Campaign Start', x: 400, y: 50, data: {} }],
    edges: []
  });
  const [selectedTemplateCampaignId, setSelectedTemplateCampaignId] = useState('');
  const [copiedFromCampaign, setCopiedFromCampaign] = useState(null);
  const [workflowBuilderKey, setWorkflowBuilderKey] = useState(0);

  // Working hours are now managed in Account Settings page

  // Stats state
  const [stats, setStats] = useState(null);

  // Daily limits dashboard state
  const [dailyLimits, setDailyLimits] = useState(null);
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showSettings, setShowSettings] = useState(false);
  const [limitSettings, setLimitSettings] = useState({
    max_connection_requests: 30,
    max_messages: 50,
    max_inmails: 40,
    max_profile_views: 40,
    max_follows: 40,
    max_post_likes: 40
  });
  const [leadsLists, setLeadsLists] = useState([]);
  const [showImportLeadsList, setShowImportLeadsList] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedListIds, setSelectedListIds] = useState([]); // For multi-select
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Sender selection (decides which LinkedIn account the campaign is bound to)
  const [senders, setSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState(accountId || null);

  // Default sender follows the global accountId context
  useEffect(() => {
    if (accountId) {
      setSelectedSenderId(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (onCreationStateChange) {
      onCreationStateChange(view === 'create');
    }
  }, [view, onCreationStateChange]);

  useEffect(() => () => {
    if (onCreationStateChange) {
      onCreationStateChange(false);
    }
  }, [onCreationStateChange]);

  const loadCampaigns = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadCampaigns: accountId 为空，跳过加载', {
        propAccountId,
        storedAccountId: localStorage.getItem('accountId'),
        storedLinkedinAccountId: localStorage.getItem('linkedin_account_id')
      });
      return;
    }

    try {
      console.log('📋 loadCampaigns: 开始加载，accountId:', accountId);
      console.log('📤 loadCampaigns: API 请求参数:', { account_id: accountId });
      const response = await axios.get(`${API_BASE_URL}/campaigns`, {
        params: { account_id: accountId }
      });
      console.log('📥 loadCampaigns: API 响应:', {
        success: response.data.success,
        campaignsCount: response.data.campaigns?.length || 0,
        responseData: response.data
      });

      if (response.data.success) {
        setCampaigns(response.data.campaigns || []);
        console.log(`✅ loadCampaigns: 成功加载 ${response.data.campaigns?.length || 0} 个 campaigns`);
      } else {
        console.warn('⚠️ loadCampaigns: API 返回 success=false:', response.data);
        alert('加载 campaigns 失败: ' + (response.data.message || '未知错误'));
      }
    } catch (error) {
      console.error('❌ loadCampaigns 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        accountId
      });
      const errorMessage = error.response?.data?.error || error.message || '未知错误';
      alert('加载 campaigns 失败: ' + errorMessage);
    }
  }, [accountId, propAccountId]);

  const loadSettings = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadSettings: accountId 为空，跳过加载');
      return;
    }

    try {
      console.log('⚙️ loadSettings: 开始加载，accountId:', accountId);
      const response = await axios.get(`${API_BASE_URL}/workflow/settings/${accountId}`);
      if (response.data.success) {
        setLimitSettings(response.data.settings || {
          max_connection_requests: 30,
          max_messages: 50,
          max_inmails: 40,
          max_profile_views: 40,
          max_follows: 40,
          max_post_likes: 40
        });
        console.log('✅ loadSettings: 成功加载设置');
      } else {
        console.warn('⚠️ loadSettings: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadSettings 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId]);

  const saveSettings = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/workflow/settings/${accountId}`, limitSettings);
      if (response.data.success) {
        alert('Settings saved successfully!');
        setShowSettings(false);
        loadDailyLimits(); // Refresh dashboard
      }
    } catch (error) {
      console.error('Save settings error:', error);
      alert('Failed to save settings: ' + error.message);
    }
  };

  const loadDailyLimits = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadDailyLimits: accountId 为空，跳过加载');
      return;
    }

    try {
      console.log('📊 loadDailyLimits: 开始加载，accountId:', accountId, 'timezone:', userTimezone);
      const response = await axios.get(`${API_BASE_URL}/workflow/daily-limits/${accountId}`, {
        params: { timezone: userTimezone }
      });
      if (response.data.success) {
        setDailyLimits(response.data);
        console.log('✅ loadDailyLimits: 成功加载每日限制数据');
      } else {
        console.warn('⚠️ loadDailyLimits: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadDailyLimits 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId, userTimezone]);


  const loadSenders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/linkedin/accounts`, { timeout: 15000 });
      if (response.data.success) {
        setSenders(response.data.senders || []);
      }
    } catch (error) {
      console.warn('⚠️ loadSenders failed:', error.message);
    }
  }, []);

  const loadLeadsLists = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadLeadsLists: accountId 为空，跳过加载');
      return;
    }

    try {
      console.log('📁 loadLeadsLists: 开始加载，accountId:', accountId);
      const response = await axios.get(`${API_BASE_URL}/leads-lists`, {
        params: { account_id: accountId }
      });
      if (response.data.success) {
        setLeadsLists(response.data.lists || []);
        console.log(`✅ loadLeadsLists: 成功加载 ${response.data.lists?.length || 0} 个列表`);
      } else {
        console.warn('⚠️ loadLeadsLists: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadLeadsLists 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId]);


  useEffect(() => {
    console.log('🔔 CampaignPage useEffect 触发:', {
      accountId,
      accountIdValid: accountId && accountId !== '',
      propAccountId,
      userTimezone
    });

    if (accountId && accountId !== '') {
      console.log('✅ CampaignPage: accountId 有效，开始加载数据');
      loadCampaigns();
      loadSettings();
      loadDailyLimits();
      loadLeadsLists();
      loadSenders();

      // Refresh daily limits every minute
      const interval = setInterval(() => {
        loadDailyLimits();
      }, 60000); // 1 minute

      // Check if date changed (for daily reset)
      const checkDateChange = setInterval(() => {
        const now = new Date();
        const currentDate = now.toLocaleDateString('en-CA', { timeZone: userTimezone });
        const lastDate = localStorage.getItem('lastDailyLimitDate');

        if (lastDate !== currentDate) {
          localStorage.setItem('lastDailyLimitDate', currentDate);
          loadDailyLimits();
        }
      }, 60000); // Check every minute

      return () => {
        clearInterval(interval);
        clearInterval(checkDateChange);
      };
    } else {
      console.warn('⚠️ CampaignPage: accountId 无效，跳过数据加载');
      console.warn('   提示：请确保已登录，或检查 localStorage 中的 accountId');
    }
  }, [accountId, userTimezone, loadCampaigns, loadSettings, loadDailyLimits, loadLeadsLists, loadSenders, propAccountId]);

  // Debug: Track selectedListId changes
  useEffect(() => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔔 STATE CHANGE: selectedListId');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('   New selectedListId:', selectedListId, 'type:', typeof selectedListId);
    console.log('   Current view:', view);
    console.log('   leadsLists count:', leadsLists.length);
    if (selectedListId !== null && selectedListId !== undefined) {
      const foundList = leadsLists.find(l => {
        const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
        const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
        return lId === sId;
      });
      if (foundList) {
        console.log('   ✅ Matching list found:', foundList.name);
      } else {
        console.warn('   ⚠️ No matching list found for ID:', selectedListId);
        console.warn('   Available IDs:', leadsLists.map(l => l.id));
      }
    } else {
      console.log('   ⚠️ selectedListId is null/undefined');
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }, [selectedListId, view, leadsLists]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        console.log('🖱️ Clicked outside dropdown, closing...');
        setShowDropdown(false);
        // Don't reset selectedListId when clicking outside
      }
    };

    if (showDropdown) {
      // Use capture phase to catch events before they bubble
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [showDropdown]);

  const handleImportLeadsList = async (campaignId, listIds) => {
    if (!listIds || listIds.length === 0) {
      alert('请至少选择一个 leads list');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/campaigns/${campaignId}/targets/from-leads-list`, {
        list_ids: listIds // Send array of list IDs
      });
      if (response.data.success) {
        const { added_count, skipped, lists_imported } = response.data;
        let message = `✅ 成功导入 ${added_count} 个目标！\n\n`;
        if (lists_imported && lists_imported.length > 0) {
          message += `已导入的列表：\n${lists_imported.map(l => `- ${l.name} (${l.added} 个)`).join('\n')}\n\n`;
        }
        if (skipped > 0) {
          message += `跳过重复项：${skipped} 个`;
        }
        alert(message);
        setShowImportLeadsList(false);
        setSelectedListIds([]);
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Import leads list error:', error);
      alert('导入失败: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCreateCampaign = async () => {
    // Validation
    if (!campaignName || campaignName.trim() === '') {
      alert('Please enter a campaign name');
      return;
    }

    // Validate workflow: check if start node has at least one child (tree structure)
    if (!workflow || !workflow.nodes || workflow.nodes.length === 0) {
      alert('Please add at least one action to the workflow');
      return;
    }

    const startNode = workflow.nodes[0];
    const hasActions = startNode && (
      startNode.child ||      // Regular child node
      startNode.yesChild ||   // Condition YES branch
      startNode.noChild       // Condition NO branch
    );

    if (!hasActions) {
      alert('Please add at least one action to the workflow');
      return;
    }

    // Validate leads list selection
    console.log('🔍 Creating campaign - selectedListId:', selectedListId, 'type:', typeof selectedListId);
    console.log('🔍 Available leadsLists:', leadsLists);

    if (!selectedListId || selectedListId === null || selectedListId === undefined) {
      alert('Please select a leads list first. Leads list is required to create a campaign.');
      return;
    }

    // Verify the selected list exists
    const selectedList = leadsLists.find(l => l.id === selectedListId || l.id === parseInt(selectedListId));
    if (!selectedList) {
      alert(`Selected leads list (ID: ${selectedListId}) not found. Please select a valid leads list.`);
      return;
    }

    console.log('✅ Selected list verified:', selectedList.name, 'ID:', selectedList.id);

    // Determine the sender (LinkedIn account) this campaign is bound to.
    const senderAccountId = selectedSenderId || accountId;
    if (!senderAccountId) {
      alert('Please pick a sender (LinkedIn account) to run this campaign.');
      return;
    }

    try {
      const settings = {};

      // Add leadsListId to workflow
      const workflowWithList = {
        ...workflow,
        leadsListId: selectedListId,
        leadsListName: selectedList.name
      };

      console.log('📤 Creating campaign with data:', {
        account_id: senderAccountId,
        name: campaignName,
        workflow_nodes: workflow.nodes.length,
        selectedListId: selectedListId,
        workflow_leadsListId: workflowWithList.leadsListId
      });

      // Step 1: Create campaign
      const response = await axios.post(`${API_BASE_URL}/campaigns`, {
        account_id: senderAccountId,
        name: campaignName,
        description: campaignDescription,
        workflow: workflowWithList,
        settings
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create campaign');
      }

      const campaignId = response.data.campaign_id;
      console.log('✅ Campaign created with ID:', campaignId);

      // Step 2: Import leads list (required)
      console.log('📥 Importing leads list:', selectedListId, 'for campaign:', campaignId);

      try {
        const importResponse = await axios.post(`${API_BASE_URL}/campaigns/${campaignId}/targets/from-leads-list`, {
          list_id: selectedListId
        });

        if (importResponse.data.success) {
          const addedCount = importResponse.data.added_count || 0;
          const skippedCount = importResponse.data.skipped || 0;
          console.log('✅ Imported', addedCount, 'targets, skipped', skippedCount);

          if (addedCount > 0) {
            alert(`✅ Campaign created successfully!\n\nImported ${addedCount} target(s) from "${selectedList.name}".\n${skippedCount > 0 ? `Skipped ${skippedCount} duplicate(s).` : ''}`);
          } else {
            alert(`⚠️ Campaign created, but no new targets were imported.\n\nThis may be because:\n- All leads from "${selectedList.name}" are already in this campaign\n- The leads list is empty\n\nYou can add more targets manually later.`);
          }
        } else {
          throw new Error(importResponse.data.message || 'Import failed');
        }
      } catch (importError) {
        console.error('❌ Import leads list error:', importError);
        const errorMsg = importError.response?.data?.message || importError.message || 'Unknown error';
        alert(`⚠️ Campaign created successfully, but failed to import leads list:\n\n${errorMsg}\n\nYou can import leads list manually from the campaign page.`);
      }

      // Step 3: Refresh and reset
      await loadCampaigns();
      setView('list');
      resetForm();
      setSelectedListId(null);

    } catch (error) {
      console.error('❌ Create campaign error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to create campaign:\n\n${errorMsg}`);
    }
  };

  const [isStarting, setIsStarting] = useState(false);

  const handleStartCampaign = async (campaignId) => {
    console.log('🚀 handleStartCampaign called:', { campaignId, accountId });

    if (!accountId) {
      alert('❌ Account ID 未设置，无法启动 Campaign。请刷新页面重试。');
      console.error('❌ Account ID is empty:', { propAccountId, localStorage: localStorage.getItem('accountId') });
      return;
    }

    if (!campaignId) {
      alert('❌ Campaign ID 无效');
      console.error('❌ Campaign ID is empty');
      return;
    }


    // Temporarily disabled confirmation dialog due to browser blocking
    // console.log('💬 Showing confirmation dialog...');
    // const confirmed = window.confirm('Start this campaign? Tasks will be scheduled and executed automatically.');
    // console.log('💬 User response:', confirmed ? 'Confirmed' : 'Cancelled');
    // 
    // if (!confirmed) {
    //   console.log('⚠️ User cancelled the start operation');
    //   return;
    // }

    console.log('✅ Proceeding to start campaign (confirmation dialog bypassed)');



    setIsStarting(true);

    try {
      console.log(`📤 Sending POST request to: ${API_BASE_URL}/workflow/start/${campaignId}`);
      const response = await axios.post(`${API_BASE_URL}/workflow/start/${campaignId}`);
      console.log('✅ Start campaign response:', response.data);

      if (response.data.success) {
        alert(response.data.message);
        // Force refresh campaigns to update status
        await loadCampaigns();
        // Also refresh after a short delay to ensure backend has updated
        setTimeout(() => {
          loadCampaigns();
        }, 500);
      } else {
        alert('启动失败: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Start campaign error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      alert('Failed to start campaign: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsStarting(false);
    }
  };

  const handleExecuteNow = async (campaignId) => {
    console.log('⚡ handleExecuteNow called:', { campaignId });
    const campaign = campaigns.find(c => c.id === campaignId);

    // If campaign is draft, start it first
    if (campaign && campaign.status === 'draft') {
      // Temporarily disabled confirmation dialog
      // if (!window.confirm('Campaign 还未启动。是否先启动 Campaign，然后立即执行所有任务？')) {
      //   return;
      // }
      console.log('⚠️ Campaign is draft, starting it first...');

      try {
        // Start campaign first
        const startResponse = await axios.post(`${API_BASE_URL}/workflow/start/${campaignId}`);
        if (!startResponse.data.success) {
          alert('启动 Campaign 失败: ' + (startResponse.data.message || 'Unknown error'));
          return;
        }

        // Refresh campaigns to get updated status
        await loadCampaigns();

        // Wait a bit for tasks to be created
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Start campaign error:', error);
        alert('启动 Campaign 失败: ' + (error.response?.data?.message || error.message));
        return;
      }
    } else {
      // Temporarily disabled confirmation dialog
      // if (!window.confirm('立即执行所有待处理的任务？这将立即运行campaign中的所有pending任务，不等待计划时间。')) {
      //   return;
      // }
      console.log('✅ Proceeding to execute tasks immediately...');
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/workflow/execute-now/${campaignId}`);
      if (response.data.success) {
        const { executed_count, failed_count, total_count } = response.data;
        if (total_count === 0) {
          alert('⚠️ 没有待执行的任务。请确保 Campaign 已启动并且有目标。');
        } else {
          alert(`✅ 执行完成！\n\n成功: ${executed_count} 个任务\n失败: ${failed_count} 个任务\n总计: ${total_count} 个任务`);
        }
        await loadCampaigns();
        // Also refresh after a short delay to ensure backend has updated
        setTimeout(() => {
          loadCampaigns();
        }, 500);
      } else {
        alert('执行失败: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Execute now error:', error);
      alert('执行失败: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/workflow/pause/${campaignId}`);
      if (response.data.success) {
        alert('Campaign paused');
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Pause campaign error:', error);
      alert('Failed to pause campaign: ' + error.message);
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/workflow/resume/${campaignId}`);
      if (response.data.success) {
        alert('Campaign resumed');
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Resume campaign error:', error);
      alert('Failed to resume campaign: ' + error.message);
    }
  };

  const handleStopCampaign = async (campaignId) => {
    if (!window.confirm('Stop this campaign? Pending tasks will be cancelled.')) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/workflow/stop/${campaignId}`);
      if (response.data.success) {
        alert('Campaign stopped');
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Stop campaign error:', error);
      alert('Failed to stop campaign: ' + error.message);
    }
  };

  const handleEditCampaign = async (campaignId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/campaigns/${campaignId}`);
      if (response.data.success) {
        const campaign = response.data.campaign;
        setCurrentCampaign(campaign);
        setCampaignName(campaign.name);
        setCampaignDescription(campaign.description || '');
        setWorkflow(campaign.workflow);

        // Restore selectedListIds from lead_lists or workflow
        if (campaign.lead_lists && campaign.lead_lists.length > 0) {
          // Use lead_lists from campaign (preferred, as it's the source of truth)
          const listIds = campaign.lead_lists.map(list => list.id);
          setSelectedListIds(listIds);
          // Also set selectedListId for backward compatibility (use first one)
          setSelectedListId(listIds[0] || null);
        } else if (campaign.workflow && campaign.workflow.leadsListId) {
          // Fallback to workflow.leadsListId
          setSelectedListId(campaign.workflow.leadsListId);
          setSelectedListIds([campaign.workflow.leadsListId]);
        } else if (campaign.workflow && campaign.workflow.leadsListIds && Array.isArray(campaign.workflow.leadsListIds)) {
          // Fallback to workflow.leadsListIds (multiple)
          setSelectedListIds(campaign.workflow.leadsListIds);
          setSelectedListId(campaign.workflow.leadsListIds[0] || null);
        } else {
          setSelectedListId(null);
          setSelectedListIds([]);
        }

        // Working hours are now managed in Account Settings page

        setView('edit');
      }
    } catch (error) {
      console.error('Load campaign for edit error:', error);
      alert('Failed to load campaign: ' + error.message);
    }
  };

  const handleUpdateCampaign = async () => {
    // Validation
    if (!campaignName || campaignName.trim() === '') {
      alert('Please enter a campaign name');
      return;
    }

    // Validate workflow: check if start node has at least one child (tree structure)
    if (!workflow || !workflow.nodes || workflow.nodes.length === 0) {
      alert('Please add at least one action to the workflow');
      return;
    }

    const startNode = workflow.nodes[0];
    const hasActions = startNode && (
      startNode.child ||      // Regular child node
      startNode.yesChild ||   // Condition YES branch
      startNode.noChild       // Condition NO branch
    );

    if (!hasActions) {
      alert('Please add at least one action to the workflow');
      return;
    }

    if (!currentCampaign) {
      alert('No campaign selected for update');
      return;
    }

    try {
      const settings = {};

      // Add leadsListIds to workflow if selected (support multiple lists)
      let workflowWithList = { ...workflow };

      if (selectedListIds && selectedListIds.length > 0) {
        // Get list names for all selected lists
        const selectedListNames = selectedListIds.map(id => {
          const list = leadsLists.find(l => {
            const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
            const sId = typeof id === 'string' ? parseInt(id, 10) : id;
            return lId === sId;
          });
          return list?.name || '';
        }).filter(name => name); // Remove empty names

        workflowWithList.leadsListIds = selectedListIds;
        workflowWithList.leadsListNames = selectedListNames;

        // Also set single leadsListId for backward compatibility (use first one)
        if (selectedListIds.length > 0) {
          workflowWithList.leadsListId = selectedListIds[0];
          workflowWithList.leadsListName = selectedListNames[0] || '';
        }
      } else if (selectedListId) {
        // Fallback to single selectedListId (for backward compatibility)
        const listName = leadsLists.find(l => {
          const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
          const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
          return lId === sId;
        })?.name || '';

        workflowWithList.leadsListId = selectedListId;
        workflowWithList.leadsListName = listName;
        workflowWithList.leadsListIds = [selectedListId];
        workflowWithList.leadsListNames = [listName];
      }

      const response = await axios.put(`${API_BASE_URL}/campaigns/${currentCampaign.id}`, {
        name: campaignName,
        description: campaignDescription,
        workflow: workflowWithList,
        settings
      });

      if (response.data.success) {
        alert('Campaign updated successfully!');
        await loadCampaigns();
        setView('list');
        resetForm();
        setCurrentCampaign(null);
      }
    } catch (error) {
      console.error('Update campaign error:', error);
      alert('Failed to update campaign: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    // Find campaign to check status
    const campaign = campaigns.find(c => c.id === campaignId);
    const campaignStatus = campaign?.status || 'unknown';

    // Warn if campaign is active
    let confirmMessage = '确定要删除这个 Campaign 吗？此操作无法撤销，所有相关的 targets 和 tasks 都会被删除。';
    if (campaignStatus === 'active') {
      confirmMessage = '⚠️ 这个 Campaign 正在运行中。删除前会先停止它。\n\n确定要删除吗？此操作无法撤销，所有相关的 targets 和 tasks 都会被删除。';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // If campaign is active, stop it first
      if (campaignStatus === 'active') {
        try {
          await axios.post(`${API_BASE_URL}/workflow/stop/${campaignId}`);
          console.log('Campaign stopped before deletion');
        } catch (stopError) {
          console.warn('Failed to stop campaign before deletion:', stopError);
          // Continue with deletion anyway
        }
      }

      const response = await axios.delete(`${API_BASE_URL}/campaigns/${campaignId}`);
      if (response.data.success) {
        alert('✅ Campaign 删除成功');
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Delete campaign error:', error);
      alert('删除 Campaign 失败: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAddTargets = async (campaignId) => {
    // Get targets from search results (stored in sessionStorage)
    const searchResults = JSON.parse(sessionStorage.getItem('search_results') || '[]');

    if (searchResults.length === 0) {
      alert('No search results found. Please perform a search first.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/campaigns/${campaignId}/targets`, {
        profiles: searchResults
      });

      if (response.data.success) {
        alert(response.data.message);
        await loadCampaigns();
      }
    } catch (error) {
      console.error('Add targets error:', error);
      alert('Failed to add targets: ' + error.message);
    }
  };

  const handleViewStats = async (campaignId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/workflow/stats/${campaignId}`);
      if (response.data.success) {
        setStats(response.data);
        // Use campaign data from response to include lead list info
        setCurrentCampaign(prev => ({
          ...(response.data.campaign || {}),
          lead_lists: response.data.campaign?.lead_lists || []
        }));
        setView('stats');
      }
    } catch (error) {
      console.error('Get stats error:', error);
      alert('Failed to load stats: ' + error.message);
    }
  };

  const resetForm = () => {
    setCampaignName('');
    setCampaignDescription('');
    setWorkflow({
      nodes: [{ id: 'start', type: 'start', label: 'Campaign Start', x: 400, y: 50, data: {} }],
      edges: []
    });
    setSelectedListId(null);
    setSelectedListIds([]);
    setShowDropdown(false);
    setSelectedTemplateCampaignId('');
    setCopiedFromCampaign(null);
    setWorkflowBuilderKey(key => key + 1);
  };

  const reusableCampaigns = campaigns
    .filter(campaign => countWorkflowSteps(campaign.workflow) > 0)
    .slice()
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const selectedTemplateCampaign = reusableCampaigns.find(
    campaign => String(campaign.id) === String(selectedTemplateCampaignId)
  );

  const handleApplyCampaignTemplate = () => {
    if (!selectedTemplateCampaign) {
      return;
    }

    const currentStartNode = workflow?.nodes?.[0];
    const hasCurrentActions = Boolean(
      currentStartNode?.child || currentStartNode?.yesChild || currentStartNode?.noChild
    );

    if (hasCurrentActions) {
      const confirmed = window.confirm(
        'Replace the workflow currently on the canvas with this campaign template?'
      );
      if (!confirmed) {
        return;
      }
    }

    const clonedWorkflow = cloneCampaignWorkflow(selectedTemplateCampaign.workflow);
    if (!clonedWorkflow) {
      alert('This campaign does not contain a reusable workflow.');
      return;
    }

    setWorkflow(clonedWorkflow);
    setCopiedFromCampaign(selectedTemplateCampaign);
    setWorkflowBuilderKey(key => key + 1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { text: 'Draft', class: 'status-draft' },
      active: { text: 'Active', class: 'status-active' },
      paused: { text: 'Paused', class: 'status-paused' },
      stopped: { text: 'Stopped', class: 'status-stopped' },
      completed: { text: 'Completed', class: 'status-completed' }
    };
    const badge = badges[status] || { text: status, class: '' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  // Render different views
  const renderListView = () => (
    <div className="campaign-list">
      <div className="page-header">
        <h2>📋 Campaigns</h2>
        <button className="btn-primary" onClick={() => setView('create')}>
          ➕ Create Campaign
        </button>
      </div>

      {/* Daily Limits Dashboard */}
      {dailyLimits && (
        <div className="daily-limits-dashboard">
          <div className="dashboard-header">
            <h3>📊 Daily Action Limits</h3>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowSettings(true)}>
              ⚙️ Settings
            </button>
          </div>
          <div className="limits-grid">
            <div className="limit-card">
              <div className="limit-label">Connection Requests</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.connection_requests / dailyLimits.limits.connection_requests) * 100)}%`,
                      backgroundColor: dailyLimits.usage.connection_requests >= dailyLimits.limits.connection_requests ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.connection_requests}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.connection_requests}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.connection_requests} left)</span>
                </div>
              </div>
            </div>

            <div className="limit-card">
              <div className="limit-label">Messages</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.messages / dailyLimits.limits.messages) * 100)}%`,
                      backgroundColor: dailyLimits.usage.messages >= dailyLimits.limits.messages ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.messages}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.messages}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.messages} left)</span>
                </div>
              </div>
            </div>

            <div className="limit-card">
              <div className="limit-label">InMails</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.inmails / dailyLimits.limits.inmails) * 100)}%`,
                      backgroundColor: dailyLimits.usage.inmails >= dailyLimits.limits.inmails ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.inmails}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.inmails}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.inmails} left)</span>
                </div>
              </div>
            </div>

            <div className="limit-card">
              <div className="limit-label">Profile Views</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.profile_views / dailyLimits.limits.profile_views) * 100)}%`,
                      backgroundColor: dailyLimits.usage.profile_views >= dailyLimits.limits.profile_views ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.profile_views}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.profile_views}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.profile_views} left)</span>
                </div>
              </div>
            </div>

            <div className="limit-card">
              <div className="limit-label">Follows</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.follows / dailyLimits.limits.follows) * 100)}%`,
                      backgroundColor: dailyLimits.usage.follows >= dailyLimits.limits.follows ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.follows}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.follows}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.follows} left)</span>
                </div>
              </div>
            </div>

            <div className="limit-card">
              <div className="limit-label">Post Likes</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.usage.post_likes / dailyLimits.limits.post_likes) * 100)}%`,
                      backgroundColor: dailyLimits.usage.post_likes >= dailyLimits.limits.post_likes ? '#dc3545' : '#0073b1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.usage.post_likes}</span>
                  <span>/</span>
                  <span>{dailyLimits.limits.post_likes}</span>
                  <span className="limit-remaining">({dailyLimits.remaining.post_likes} left)</span>
                </div>
              </div>
            </div>
          </div>
          {dailyLimits.weekly && (
            <div className="weekly-limits-section">
              <div className="weekly-limits-header">📅 Weekly Connection Requests (Last 7 Days)</div>
              <div className="limit-progress">
                <div className="limit-bar">
                  <div
                    className="limit-fill"
                    style={{
                      width: `${Math.min(100, (dailyLimits.weekly.connection_requests.usage / dailyLimits.weekly.connection_requests.limit) * 100)}%`,
                      backgroundColor: dailyLimits.weekly.connection_requests.usage >= dailyLimits.weekly.connection_requests.limit ? '#dc3545' : '#6f42c1'
                    }}
                  ></div>
                </div>
                <div className="limit-numbers">
                  <span>{dailyLimits.weekly.connection_requests.usage}</span>
                  <span>/</span>
                  <span>{dailyLimits.weekly.connection_requests.limit}</span>
                  <span className="limit-remaining">({dailyLimits.weekly.connection_requests.remaining} left)</span>
                </div>
              </div>
            </div>
          )}
          <div className="limits-footer">
            <span>Timezone: {userTimezone}</span>
            <span>Date: {new Date().toLocaleDateString('en-US', { timeZone: userTimezone })}</span>
            <span>Auto-refresh: Every 1 minute</span>
          </div>
        </div>
      )}


      {campaigns.length === 0 ? (
        <div className="empty-state">
          <p>No campaigns yet. Create your first campaign!</p>
        </div>
      ) : (
        <div className="campaigns-grid">
          {campaigns.map(campaign => (
            <div key={campaign.id} className="campaign-card">
              <div className="campaign-header">
                <div>
                  <h3>{campaign.name}</h3>
                  {campaign.recurring_series_name && (
                    <span className="campaign-series-tag">Series: {campaign.recurring_series_name}</span>
                  )}
                </div>
                {getStatusBadge(campaign.status)}
              </div>

              <div className="campaign-sender" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px',
                marginBottom: '8px'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '14px',
                  height: '14px',
                  background: '#0073b1',
                  color: 'white',
                  borderRadius: '2px',
                  fontSize: '9px',
                  fontWeight: 700,
                  fontFamily: 'Arial, sans-serif'
                }}>in</span>
                <span>
                  Sender: {campaign.sender_email || campaign.sender_public_identifier || campaign.account_id}
                </span>
              </div>

              {campaign.description && (
                <p className="campaign-description">{campaign.description}</p>
              )}

              <div className="campaign-stats">
                <div className="stat-item">
                  <span className="stat-label">Targets:</span>
                  <span className="stat-value">{campaign.stats?.targets || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Tasks:</span>
                  <span className="stat-value">
                    {campaign.stats?.tasks?.completed || 0} / {campaign.stats?.tasks?.actual_total || campaign.stats?.tasks?.total || 0}
                    {campaign.stats?.tasks?.max_possible && 
                     campaign.stats?.tasks?.max_possible > (campaign.stats?.tasks?.actual_total || campaign.stats?.tasks?.total || 0) && (
                      <span className="stat-hint" title="最大可能数量（包括所有分支）">
                        {' '}(最大可能: {campaign.stats?.tasks?.max_possible})
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {campaign.lead_lists?.length > 0 && (
                <div className="lead-lists">
                  <span className="lead-lists-label">Leads Lists:</span>
                  <div className="lead-list-tags">
                    {campaign.lead_lists.map(list => (
                      <span key={list.id} className="lead-list-tag">{list.name}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="campaign-actions">
                {campaign.status === 'draft' && (
                  <>
                    <button className="btn-small" onClick={() => handleEditCampaign(campaign.id)}>
                      ✏️ Edit
                    </button>
                    <button
                      className="btn-small btn-primary"
                      onClick={(e) => {
                        console.log('🔘 Start button clicked:', { campaignId: campaign.id, accountId });
                        handleStartCampaign(campaign.id);
                      }}
                    >
                      ▶️ Start
                    </button>
                    <button className="btn-small btn-primary" onClick={() => handleExecuteNow(campaign.id)}>
                      ⚡ 立即执行
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleDeleteCampaign(campaign.id)}>
                      🗑️ Delete
                    </button>
                  </>
                )}

                {campaign.status === 'active' && (
                  <>
                    <button className="btn-small btn-primary" onClick={() => handleExecuteNow(campaign.id)}>
                      ⚡ 立即执行
                    </button>
                    <button className="btn-small" onClick={() => handlePauseCampaign(campaign.id)}>
                      ⏸️ Pause
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleStopCampaign(campaign.id)}>
                      ⏹️ Stop
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleDeleteCampaign(campaign.id)}>
                      🗑️ Delete
                    </button>
                  </>
                )}

                {campaign.status === 'paused' && (
                  <>
                    <button className="btn-small btn-primary" onClick={() => handleResumeCampaign(campaign.id)}>
                      ▶️ Resume
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleStopCampaign(campaign.id)}>
                      ⏹️ Stop
                    </button>
                    <button className="btn-small" onClick={() => handleEditCampaign(campaign.id)}>
                      ✏️ Edit
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleDeleteCampaign(campaign.id)}>
                      🗑️ Delete
                    </button>
                  </>
                )}

                {(campaign.status === 'stopped' || campaign.status === 'completed') && (
                  <>
                    <button className="btn-small" onClick={() => handleEditCampaign(campaign.id)}>
                      ✏️ Edit
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleDeleteCampaign(campaign.id)}>
                      🗑️ Delete
                    </button>
                  </>
                )}

                <button className="btn-small" onClick={() => handleViewStats(campaign.id)}>
                  📊 Stats
                </button>
                <button 
                  className="btn-small btn-info" 
                  onClick={() => navigate(`/campaigns/${campaign.id}/status`)}
                >
                  📈 Campaign Status
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Import Leads List Modal */}
      {showImportLeadsList && (
        <div className="modal-overlay" onClick={() => {
          setShowImportLeadsList(false);
          if (view !== 'create') {
            setSelectedListIds([]);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📥 {view === 'create' ? 'Select Leads List' : 'Import Leads Lists'}</h2>
            <p>{view === 'create'
              ? 'Select a leads list to import targets when creating this campaign:'
              : 'Select one or more leads lists to import targets into this campaign (支持多选):'}</p>

            {leadsLists.length === 0 ? (
              <p className="text-muted">No leads lists available. Create one in the Leads page first.</p>
            ) : (
              <div className="leads-lists-selector" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {leadsLists.map(list => {
                  const listId = typeof list.id === 'number' ? list.id : parseInt(list.id, 10);
                  const isSelected = selectedListIds.includes(listId);

                  return (
                    <div
                      key={list.id}
                      className={`list-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        if (view === 'create') {
                          // For create view, single select
                          setSelectedListId(listId);
                        } else {
                          // For import view, multi-select
                          setSelectedListIds(prev => {
                            if (prev.includes(listId)) {
                              return prev.filter(id => id !== listId);
                            } else {
                              return [...prev, listId];
                            }
                          });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        padding: '12px',
                        marginBottom: '8px',
                        border: isSelected ? '2px solid #28a745' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isSelected ? '#e6f3ff' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                    >
                      {view !== 'create' && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            setSelectedListIds(prev => {
                              if (prev.includes(listId)) {
                                return prev.filter(id => id !== listId);
                              } else {
                                return [...prev, listId];
                              }
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div className="list-option-name" style={{ fontWeight: isSelected ? 600 : 500, marginBottom: '4px' }}>
                          {list.name}
                        </div>
                        <div className="list-option-count" style={{ fontSize: '12px', color: '#666' }}>
                          {list.lead_count || 0} leads
                        </div>
                      </div>
                      {isSelected && (
                        <span style={{ color: '#28a745', fontSize: '18px' }}>✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {view !== 'create' && selectedListIds.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f8ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                  <strong>已选择 {selectedListIds.length} 个列表：</strong>
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedListIds.map(id => {
                    const list = leadsLists.find(l => {
                      const lId = typeof l.id === 'number' ? l.id : parseInt(l.id, 10);
                      return lId === id;
                    });
                    return list ? `${list.name} (${list.lead_count || 0} leads)` : '';
                  }).filter(Boolean).join(', ')}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (view === 'create') {
                    // For create view, just store the selected list ID
                    setShowImportLeadsList(false);
                  } else if (currentCampaign) {
                    handleImportLeadsList(currentCampaign.id, selectedListIds);
                  }
                }}
                disabled={view === 'create' ? !selectedListId : selectedListIds.length === 0}
              >
                {view === 'create' ? 'Select' : `Import ${selectedListIds.length > 0 ? `(${selectedListIds.length})` : ''}`}
              </button>
              <button className="btn" onClick={() => {
                setShowImportLeadsList(false);
                if (view !== 'create') {
                  setSelectedListIds([]);
                }
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderEditView = () => (
    <div className="campaign-create">
      <div className="page-header">
        <h2>✏️ Edit Campaign</h2>
        <button className="btn-secondary" onClick={() => { setView('list'); resetForm(); setCurrentCampaign(null); }}>
          ← Back
        </button>
      </div>

      <div className="form-section">
        <h3>Campaign Details</h3>
        <div className="form-group">
          <label>Campaign Name *</label>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="e.g., Software Engineers Outreach Q1"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={campaignDescription}
            onChange={(e) => setCampaignDescription(e.target.value)}
            placeholder="Campaign description..."
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>Import Targets from Leads List</label>
          {leadsLists.length === 0 ? (
            <div style={{ padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px' }}>
              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                ⚠️ No leads lists available. Please create a leads list in the <strong>Leads</strong> page first.
              </p>
            </div>
          ) : (
            <div className="custom-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🖱️ Import dropdown button clicked (Edit View)');
                  console.log('   Current showDropdown:', showDropdown);
                  console.log('   Current selectedListId:', selectedListId);
                  setShowDropdown(!showDropdown);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: selectedListId ? '2px solid #28a745' : '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {selectedListId ? (
                    (() => {
                      const selectedList = leadsLists.find(l => {
                        const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                        const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                        return lId === sId;
                      });
                      return selectedList ? `${selectedList.name} (${selectedList.lead_count || 0} leads)` : '-- Select a leads list to import --';
                    })()
                  ) : (
                    '-- Select a leads list to import --'
                  )}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {showDropdown ? '▲' : '▼'}
                </span>
              </button>

              {showDropdown && (
                <div
                  className="custom-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {leadsLists.map((list) => {
                    const listId = typeof list.id === 'number' ? list.id : parseInt(list.id, 10);
                    const isSelected = (() => {
                      const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                      return listId === sId;
                    })();

                    return (
                      <div
                        key={list.id}
                        className="custom-dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('✅ Import list selected:', listId, list.name);
                          const selectedId = listId;
                          setSelectedListId(selectedId);
                          setShowDropdown(false);

                          // Import leads list when selected
                          if (currentCampaign && selectedId) {
                            console.log('📥 Importing leads list:', selectedId, 'for campaign:', currentCampaign.id);
                            // Ensure we pass an array to handleImportLeadsList
                            handleImportLeadsList(currentCampaign.id, [selectedId]);
                          }
                        }}
                        style={{
                          padding: '12px 15px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#e6f3ff' : 'white',
                          borderLeft: isSelected ? '3px solid #0073b1' : '3px solid transparent',
                          transition: 'all 0.2s',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: isSelected ? 600 : 500, color: '#333', marginBottom: '4px' }}>
                          {list.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {list.lead_count || 0} leads
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedListId !== null && selectedListId !== undefined && (
                <p style={{ marginTop: '8px', color: '#28a745', fontSize: '12px' }}>
                  ✓ Selected: {leadsLists.find(l => {
                    const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                    const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                    return lId === sId;
                  })?.name || 'Unknown'} ({leadsLists.find(l => {
                    const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                    const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                    return lId === sId;
                  })?.lead_count || 0} leads)
                </p>
              )}
            </div>
          )}
          {currentCampaign && currentCampaign.lead_lists && currentCampaign.lead_lists.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Imported lists:</span>
              {currentCampaign.lead_lists.map(list => (
                <span key={list.id} className="lead-list-tag">{list.name}</span>
              ))}
            </div>
          )}
          <p className="form-hint">Select a leads list to import targets into this campaign.</p>
        </div>
      </div>

      <div className="form-section workflow-section">
        <h3>Workflow Designer</h3>
        <p className="section-hint">Design your outreach workflow with actions and branches</p>

        <WorkflowBuilder
          workflow={workflow}
          onChange={(updatedWorkflow) => setWorkflow(updatedWorkflow)}
        />
      </div>

      <div className="form-actions">
        <button className="btn-primary" onClick={handleUpdateCampaign}>
          💾 Update Campaign
        </button>
        <button className="btn-secondary" onClick={() => { setView('list'); resetForm(); setCurrentCampaign(null); }}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Helper function to check if campaign can be created
  const canCreateCampaign = () => {
    if (!selectedListId || !campaignName || !workflow || !workflow.nodes || workflow.nodes.length === 0) {
      return false;
    }

    // Check if workflow has at least one action (tree structure)
    const startNode = workflow.nodes[0];
    const hasActions = startNode && (
      startNode.child ||      // Regular child node
      startNode.yesChild ||   // Condition YES branch
      startNode.noChild       // Condition NO branch
    );

    return hasActions;
  };

  const renderCreateView = () => (
    <div className="campaign-create">
      <div className="page-header">
        <h2>➕ Create Campaign</h2>
        <button className="btn-secondary" onClick={() => { setView('list'); resetForm(); }}>
          ← Back
        </button>
      </div>

      <div className="form-section">
        <h3>Campaign Details</h3>
        <div className="form-group">
          <label>Campaign Name *</label>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="e.g., Software Engineers Outreach Q1"
          />
        </div>

        <div className="form-group">
          <label>Sender (LinkedIn account) *</label>
          {senders.length === 0 ? (
            <div style={{ padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px' }}>
              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                No LinkedIn accounts available. Please connect one on the <strong>Senders</strong> page.
              </p>
            </div>
          ) : (
            <select
              value={selectedSenderId || ''}
              disabled
              title="Sender is locked to the current LinkedIn account while creating a campaign"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#f3f4f6',
                cursor: 'not-allowed'
              }}
            >
              {senders.map(s => (
                <option key={s.account_id} value={s.account_id}>
                  {s.name}{s.email ? ` · ${s.email}` : ''}{s.active_campaigns > 0 ? ` (in ${s.active_campaigns} campaign${s.active_campaigns > 1 ? 's' : ''})` : ''}
                </option>
              ))}
            </select>
          )}
          <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
            Sender is locked to the current LinkedIn account while creating this campaign.
          </p>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={campaignDescription}
            onChange={(e) => setCampaignDescription(e.target.value)}
            placeholder="Campaign description..."
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>Select Leads List *</label>
          {leadsLists.length === 0 ? (
            <div style={{ padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px' }}>
              <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                ⚠️ No leads lists available. Please create a leads list in the <strong>Leads</strong> page first.
              </p>
            </div>
          ) : (
            <div className="custom-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: selectedListId ? '2px solid #28a745' : '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {selectedListId ? (
                    (() => {
                      const selectedList = leadsLists.find(l => {
                        const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                        const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                        return lId === sId;
                      });
                      return selectedList ? `${selectedList.name} (${selectedList.lead_count || 0} leads)` : '-- Select a leads list --';
                    })()
                  ) : (
                    '-- Select a leads list --'
                  )}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {showDropdown ? '▲' : '▼'}
                </span>
              </button>

              {showDropdown && (
                <div
                  className="custom-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {leadsLists.map((list) => {
                    const listId = typeof list.id === 'number' ? list.id : parseInt(list.id, 10);
                    const isSelected = (() => {
                      const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                      return listId === sId;
                    })();

                    return (
                      <div
                        key={list.id}
                        className="custom-dropdown-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedListId(listId);
                          setShowDropdown(false);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        style={{
                          padding: '12px 15px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#e6f3ff' : 'white',
                          borderLeft: isSelected ? '3px solid #28a745' : '3px solid transparent',
                          transition: 'all 0.2s',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: isSelected ? 600 : 500, color: '#333', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isSelected && <span style={{ color: '#28a745' }}>✓</span>}
                          {list.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {list.lead_count || 0} leads
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedListId !== null && selectedListId !== undefined && (
                <p style={{ marginTop: '8px', color: '#28a745', fontSize: '12px', fontWeight: 500 }}>
                  ✓ Selected: {leadsLists.find(l => {
                    const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                    const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                    return lId === sId;
                  })?.name || 'Unknown'} ({leadsLists.find(l => {
                    const lId = typeof l.id === 'string' ? parseInt(l.id, 10) : l.id;
                    const sId = typeof selectedListId === 'string' ? parseInt(selectedListId, 10) : selectedListId;
                    return lId === sId;
                  })?.lead_count || 0} leads)
                </p>
              )}
              {(!selectedListId || selectedListId === null || selectedListId === undefined) && (
                <p style={{ marginTop: '8px', color: '#dc3545', fontSize: '12px' }}>
                  ⚠️ Required: Please select a leads list to create a campaign
                </p>
              )}
            </div>
          )}
          <p className="form-hint">You must select a leads list to create a campaign. Targets will be imported automatically when you create the campaign.</p>
        </div>
      </div>

      <div className="form-section workflow-section">
        <h3>Workflow Designer</h3>
        <p className="section-hint">Design your outreach workflow with actions and branches</p>

        <div className="campaign-template-picker">
          <div className="campaign-template-picker-header">
            <div>
              <strong>Reuse a previous campaign</strong>
              <p>Copy only its workflow. Sender, leads, status and execution history are not copied.</p>
            </div>
          </div>

          {reusableCampaigns.length === 0 ? (
            <p className="campaign-template-empty">No previous campaigns with reusable workflows yet.</p>
          ) : (
            <div className="campaign-template-controls">
              <select
                value={selectedTemplateCampaignId}
                onChange={(event) => setSelectedTemplateCampaignId(event.target.value)}
              >
                <option value="">-- Select a previous campaign --</option>
                {reusableCampaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({countWorkflowSteps(campaign.workflow)} steps)
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary"
                disabled={!selectedTemplateCampaign}
                onClick={handleApplyCampaignTemplate}
              >
                Use this workflow
              </button>
            </div>
          )}

          {selectedTemplateCampaign && (
            <div className="campaign-template-summary">
              <span>{countWorkflowSteps(selectedTemplateCampaign.workflow)} workflow steps</span>
              <span>Status: {selectedTemplateCampaign.status || 'unknown'}</span>
              {selectedTemplateCampaign.created_at && (
                <span>Created: {new Date(selectedTemplateCampaign.created_at).toLocaleDateString()}</span>
              )}
            </div>
          )}

          {copiedFromCampaign && (
            <p className="campaign-template-applied">
              ✓ Workflow copied from &quot;{copiedFromCampaign.name}&quot;. You can edit every step below.
            </p>
          )}
        </div>

        <WorkflowBuilder
          key={`create-workflow-${workflowBuilderKey}`}
          workflow={workflow}
          onChange={(updatedWorkflow) => setWorkflow(updatedWorkflow)}
        />
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={() => {
          setView('list');
          resetForm();
          setSelectedListId(null);
        }}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={handleCreateCampaign}
          disabled={!canCreateCampaign()}
          style={{
            opacity: canCreateCampaign() ? 1 : 0.5,
            cursor: canCreateCampaign() ? 'pointer' : 'not-allowed'
          }}
        >
          Create Campaign
        </button>
      </div>
    </div>
  );

  const renderStatsView = () => {
    if (!stats || !currentCampaign) return null;

    return (
      <div className="campaign-stats-view">
        <div className="page-header">
          <h2>📊 Campaign Statistics</h2>
          <button className="btn-secondary" onClick={() => setView('list')}>
            ← Back
          </button>
        </div>

        <div className="stats-header">
          <h3>{currentCampaign.name}</h3>
          {getStatusBadge(currentCampaign.status)}
        </div>

        {currentCampaign.lead_lists?.length > 0 && (
          <div className="lead-lists stats-lead-lists">
            <span className="lead-lists-label">Leads Lists:</span>
            <div className="lead-list-tags">
              {currentCampaign.lead_lists.map(list => (
                <span key={list.id} className="lead-list-tag">{list.name}</span>
              ))}
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <h4>Targets</h4>
            <div className="stat-breakdown">
              <div className="stat-row">
                <span>Total:</span>
                <span className="stat-number">{stats.targets.total}</span>
              </div>
              <div className="stat-row">
                <span>In Progress:</span>
                <span className="stat-number">{stats.targets.in_progress}</span>
              </div>
              <div className="stat-row">
                <span>Completed:</span>
                <span className="stat-number stat-success">{stats.targets.completed}</span>
              </div>
              <div className="stat-row">
                <span>Failed:</span>
                <span className="stat-number stat-error">{stats.targets.failed}</span>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <h4>Tasks</h4>
            <div className="stat-breakdown">
              <div className="stat-row">
                <span>Total:</span>
                <span className="stat-number">
                  {stats.tasks.actual_total || stats.tasks.total}
                  {stats.tasks.max_possible && 
                   stats.tasks.max_possible > (stats.tasks.actual_total || stats.tasks.total) && (
                    <span className="stat-hint" title="最大可能数量（包括所有分支）">
                      {' '}(最大可能: {stats.tasks.max_possible})
                    </span>
                  )}
                </span>
              </div>
              <div className="stat-row">
                <span>Pending:</span>
                <span className="stat-number">{stats.tasks.pending}</span>
              </div>
              <div className="stat-row">
                <span>Completed:</span>
                <span className="stat-number stat-success">{stats.tasks.completed}</span>
              </div>
              <div className="stat-row">
                <span>Failed:</span>
                <span className="stat-number stat-error">{stats.tasks.failed}</span>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <h4>Today's Activity</h4>
            <div className="stat-breakdown">
              <div className="stat-row">
                <span>Connection Requests:</span>
                <span className="stat-number">
                  {stats.today_limits.connection_requests || 0} / 30
                </span>
              </div>
              <div className="stat-row">
                <span>Messages:</span>
                <span className="stat-number">
                  {stats.today_limits.messages || 0} / 50
                </span>
              </div>
              <div className="stat-row">
                <span>InMails:</span>
                <span className="stat-number">
                  {stats.today_limits.inmails || 0} / 40
                </span>
              </div>
              <div className="stat-row">
                <span>Profile Views:</span>
                <span className="stat-number">
                  {stats.today_limits.profile_views || 0} / 40
                </span>
              </div>
            </div>
          </div>

          <div className="stat-card full-width">
            <h4>Actions Breakdown (按 Workflow 顺序)</h4>
            <div className="actions-breakdown">
              {stats.actions.map((action, index) => {
                // Map action types to friendly names
                const actionNames = {
                  'connection_request': '🤝 Send Connection Request',
                  'send_message': '💬 Send Message',
                  'inmail': '📧 Send InMail',
                  'view_profile': '👁️ View Profile',
                  'follow': '➕ Follow',
                  'like_post': '👍 Like Post',
                  'delay': '⏰ Delay',
                  'connection_request_condition': '🔀 Check Connection Accepted',
                  'message_condition': '💭 Check Message Replied',
                  'end': '✓ End'
                };

                const actionName = actionNames[action.action_type] || action.action_type;

                return (
                  <div key={action.action_type} className="action-stat" style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#0073b1',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {index + 1}
                      </span>
                      <span className="action-type" style={{ fontWeight: 500 }}>{actionName}</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${Math.min(100, (action.completed / action.total) * 100)}%` }}
                      ></div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span className="action-numbers" style={{ fontSize: '12px', color: '#666' }}>
                        {action.completed} / {action.total} 完成
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {action.total > 0 ? Math.round((action.completed / action.total) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {stats.recent_history.length > 0 && (
          <div className="recent-history">
            <h4>Recent Activity</h4>
            <div className="history-list">
              {stats.recent_history.map((item, index) => {
                // Check if this is a pending message_condition task
                const isPending = item.is_pending ||
                  (item.action_type === 'message_condition' && !item.success && item.status === 'pending');

                return (
                  <div key={index} className={`history-item ${isPending ? 'pending' : (item.success ? 'success' : 'failed')
                    }`}>
                    <span className="history-icon">
                      {isPending ? (
                        <span className="spinner">⟳</span>
                      ) : (
                        item.success ? '✓' : '✗'
                      )}
                    </span>
                    <span className="history-action">
                      {item.action_type === 'connection_request_condition' ? 'Connection Request Condition' :
                        item.action_type === 'message_condition' ? 'Message Condition' :
                          item.action_type === 'connection_request' ? 'Connection Request' :
                            item.action_type === 'send_message' ? 'Send Message' :
                              item.action_type}
                    </span>
                    <span className="history-time">
                      {new Date(item.executed_at || item.created_at).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 如果 accountId 无效，显示错误提示
  if (!accountId || accountId === '') {
    return (
      <div className="campaign-page">
        <div className="error-state" style={{ padding: '40px', textAlign: 'center' }}>
          <h2>⚠️ 无法加载数据</h2>
          <p>accountId 无效或未设置。请确保：</p>
          <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '20px' }}>
            <li>已成功登录 LinkedIn 账号</li>
            <li>localStorage 中包含有效的 accountId</li>
          </ul>
          <p style={{ marginTop: '20px', color: '#666' }}>
            调试信息：propAccountId = {propAccountId || 'null'},
            localStorage.accountId = {localStorage.getItem('accountId') || 'null'},
            localStorage.linkedin_account_id = {localStorage.getItem('linkedin_account_id') || 'null'}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px' }}
          >
            刷新页面
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-page">
      {view === 'list' && renderListView()}
      {view === 'create' && renderCreateView()}
      {view === 'edit' && renderEditView()}
      {view === 'stats' && renderStatsView()}

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>⚙️ Daily Action Limits Settings</h2>
            <p className="settings-description">Configure your daily action limits. These limits help protect your account from being flagged.</p>

            <div className="settings-form">
              <div className="setting-item">
                <label>Max Connection Requests/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={limitSettings.max_connection_requests}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_connection_requests: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={limitSettings.max_connection_requests}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_connection_requests: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
                <div className="setting-warning">
                  ⚠️ Sending <strong>too many connection requests</strong> per day may put your account in a <strong>cooldown period</strong>. Keep this in mind when increasing this limit.
                </div>
              </div>

              <div className="setting-item">
                <label>Max Messages/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={limitSettings.max_messages}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_messages: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={limitSettings.max_messages}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_messages: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
              </div>

              <div className="setting-item">
                <label>Max InMail Messages/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={limitSettings.max_inmails}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_inmails: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={limitSettings.max_inmails}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_inmails: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
              </div>

              <div className="setting-item">
                <label>Max Profile Views/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={limitSettings.max_profile_views}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_profile_views: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="200"
                    value={limitSettings.max_profile_views}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_profile_views: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
              </div>

              <div className="setting-item">
                <label>Max Follows/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={limitSettings.max_follows}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_follows: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={limitSettings.max_follows}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_follows: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
              </div>

              <div className="setting-item">
                <label>Max Post Likes/day</label>
                <div className="setting-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={limitSettings.max_post_likes}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_post_likes: parseInt(e.target.value) })}
                    className="setting-slider"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={limitSettings.max_post_likes}
                    onChange={(e) => setLimitSettings({ ...limitSettings, max_post_likes: parseInt(e.target.value) || 0 })}
                    className="setting-input"
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={saveSettings}>
                Save settings
              </button>
              <button className="btn" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignPage;
